import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { FormDialog } from "components/dialogs/Dialogs";
import { Checkbox, FormGroup, showSnackbar, Textarea } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { Ship } from "view/ships/types/ship";
import { Contact } from "types/commons";

import { gql } from "__generated__/gql.ts";
import { PdfButton } from "view/charters/dossier/ship/ShipDetailsBlock";
import { CharterPdfType } from "__generated__/enums";
import { Voyage } from "view/voyages/types/voyage";
import { hasPluginPermission } from "utils";
import { useStoreState } from "ezStore";
import { Relation } from "view/relations/types/relation";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

type CargoSendUpdateDialogProps = {
	open: boolean,
	onClose: () => void,
	voyage: (Pick<Voyage, "isThirdParty" | "creditor"> & { ship: Pick<Ship, "name" | "contacts" | "operator" | "shipManager" | "skipper"> | undefined }) | undefined,
	charterId: number,
	customer: { name: string, contacts: Pick<Contact, "id" | "name" | "email">[] },
	onSuccess?: () => void,
}

type CargoSendUpdateForm = {
	includeCustomer: boolean,
	includeShipManager: boolean,
	includeShipOperator: boolean,
	includeShip: boolean,
	includePricing: boolean,
	note: string,
}
const EmailTooltip = ({ emails }: { emails: string[] }) => {
	if (emails.length === 0) {
		return null;
	}
	return (
		<Tooltip title={<>{emails.map((e, i) => <div key={i}>{e}</div>)}</>} placement="right">
			<InfoOutlinedIcon fontSize="small" color="action" sx={{ ml: 0.5, cursor: "pointer" }} />
		</Tooltip>
	);
};

const getRelationEmails = (relation: Pick<Relation, "email" | "contacts"> | null | undefined, useContactsCommunicateStatus: boolean): string[] => {
	if (useContactsCommunicateStatus) {
		return relation?.contacts?.filter(c => c.communicateStatusChanges && c.email).map(c => c.email!) ?? [];
	}
	return relation?.email ? [ relation.email ] : [];
};

const CargoSendUpdateDialog = ({ open, onClose, customer, voyage, charterId, onSuccess }: CargoSendUpdateDialogProps) => {
	const session = useStoreState(state => state.session);
	const useContactsCommunicateStatus = hasPluginPermission(session, "useRelationsForStatusCommunication");

	const defaultValues: CargoSendUpdateForm = {
		includeCustomer: customer.contacts.length > 0,
		includeShipManager: (voyage && !voyage.isThirdParty && !!voyage.ship?.shipManager) ?? false,
		includeShipOperator: (voyage && voyage.isThirdParty) ?? false,
		includeShip: (voyage && !voyage.isThirdParty) ?? false,
		includePricing: false,
		note: "",
	};

	const { control, handleSubmit, reset, watch } = useForm<CargoSendUpdateForm>({
		defaultValues,
	});

	const { includePricing, includeCustomer, includeShipManager, includeShipOperator, includeShip } = watch();

	const [ sendCargoUpdate, { loading } ] = useMutation(SEND_CARGO_UPDATE_PDF);

	const handleOnClose = () => {
		onClose();
		reset();
	};

	const onSubmit = (data: CargoSendUpdateForm) => {
		sendCargoUpdate({
			variables: {
				charterId: charterId,
				input: data,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SENT" }), { variant: "success" });
			handleOnClose();
			onSuccess?.();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CHARTER_UPDATE_EMAIL" })}
			form="cargoSendUpdateForm"
			loading={loading}
			maxWidth="xs"
			confirmText={intl.formatMessage({ id: "SEND" })}
		>
			<form onSubmit={(event) => void handleSubmit(onSubmit)(event)} id="cargoSendUpdateForm">
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<FormGroup label={intl.formatMessage({ id: "CUSTOMER" })}>
						<div style={{ display: "flex", alignItems: "center" }}>
							<Controller
								name="includeCustomer"
								control={control}
								render={({ field }) => (
									<Checkbox
										label={customer.name}
										checked={field.value}
										onChange={field.onChange}
										disabled={customer.contacts.filter(c => !!c.email).length == 0}
									/>
								)}
							/>
							<EmailTooltip emails={customer.contacts.filter(c => !!c.email).map(c => c.email!)} />
						</div>
					</FormGroup>
					<PdfButton
						charter={{ id: charterId }}
						type={CharterPdfType.NominationToCustomer}
						includePricing={includePricing}
						disabled={!includeCustomer}
					/>
				</div>
				{voyage && !voyage.isThirdParty && (
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<FormGroup label={intl.formatMessage({ id: "SHIP_MANAGER" })}>
							<div style={{ display: "flex", alignItems: "center" }}>
								<Controller
									name="includeShipManager"
									control={control}
									render={({ field }) => (
										<Checkbox
											label={voyage.ship?.shipManager?.name}
											disabled={voyage.ship?.shipManager == null}
											checked={field.value}
											onChange={field.onChange}
										/>
									)}
								/>
								<EmailTooltip emails={getRelationEmails(voyage.ship?.shipManager, useContactsCommunicateStatus)} />
							</div>
						</FormGroup>
						<PdfButton
							charter={{ id: charterId }}
							type={CharterPdfType.NominationToShipOwner}
							includePricing={includePricing}
							disabled={!includeShipManager}
						/>
					</div>
				)}
				{voyage && voyage.isThirdParty && (
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<FormGroup label={intl.formatMessage({ id: "OPERATOR" })}>
							<div style={{ display: "flex", alignItems: "center" }}>
								<Controller
									name="includeShipOperator"
									control={control}
									render={({ field }) => (
										<Checkbox
											label={voyage.creditor?.name}
											checked={field.value}
											onChange={field.onChange}
										/>
									)}
								/>
								<EmailTooltip emails={getRelationEmails(voyage.creditor, useContactsCommunicateStatus)} />
							</div>
						</FormGroup>
						<PdfButton
							charter={{ id: charterId }}
							type={CharterPdfType.NominationToShipOwner}
							includePricing={includePricing}
							disabled={!includeShipOperator}
						/>
					</div>
				)}
				{voyage && !voyage.isThirdParty && (
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<FormGroup label={intl.formatMessage({ id: "SHIP" })}>
							<div style={{ display: "flex", alignItems: "center" }}>
								<Controller
									name="includeShip"
									control={control}
									render={({ field }) => (
										<Checkbox
											label={voyage.ship?.skipper?.name}
											disabled={voyage.ship?.skipper == null}
											checked={field.value}
											onChange={field.onChange}
										/>
									)}
								/>
								<EmailTooltip emails={getRelationEmails(voyage.ship?.skipper, useContactsCommunicateStatus)} />
							</div>
						</FormGroup>
						<PdfButton
							charter={{ id: charterId }}
							type={CharterPdfType.NominationToSkipper}
							includePricing={includePricing}
							disabled={!includeShip}
						/>
					</div>
				)}
				<FormGroup label={intl.formatMessage({ id: "EMAIL_NOTE" })} tooltip={intl.formatMessage({ id: "EMAIL_NOTE_TOOLTIP" })}>
					<Controller
						name="note"
						control={control}
						render={({ field }) => (
							<Textarea {...field} />
						)}
					/>
				</FormGroup>

				<Controller
					name="includePricing"
					control={control}
					render={({ field }) => (
						<Checkbox
							{...field}
							label={intl.formatMessage({ id: "INCLUDE_PRICING_IN_NOMINATION" })}
							value={undefined}
						/>
					)}
				/>
			</form>
		</FormDialog>
	);
};

export const SEND_CARGO_UPDATE_PDF = gql(`#graphql
	mutation sendCargoUpdate($charterId: Long!, $input: SendCargoUpdateInput!) {
		sendCargoUpdate(charterId: $charterId, input: $input)
	}
`);

export default CargoSendUpdateDialog;
